import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import api from "../../api/client";

const getCityFromAddress = (address = "") => {
  const parts = String(address)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  return parts[parts.length - 1];
};

const getZipFromAddress = (address = "") => {
  const match = String(address).match(/\b\d{5,6}\b/);
  return match ? match[0] : "";
};

const BASE_COLUMNS = [
  { field: "srNo", headerName: "Sr. No.", width: 90, minWidth: 60 },
  { field: "registrationId", headerName: "Registration ID", width: 160, minWidth: 90 },
  {
    field: "contactName",
    headerName: "Contact Name",
    width: 190,
    minWidth: 90,
    cellClassName: "name-column--cell",
  },
  {
    field: "brandName",
    headerName: "Brand Name",
    headerAlign: "left",
    align: "left",
    width: 190,
    minWidth: 90,
  },
  { field: "phoneNumber", headerName: "Phone Number", width: 150, minWidth: 90 },
  { field: "emailId", headerName: "Email ID", width: 220, minWidth: 100 },
  { field: "contactPerson", headerName: "Contact Person", width: 180, minWidth: 90 },
  { field: "website", headerName: "Website", width: 230, minWidth: 100 },
  { field: "productDescription", headerName: "Product Description", width: 220, minWidth: 100 },
  { field: "address", headerName: "Address", width: 220, minWidth: 100 },
  { field: "city", headerName: "City", width: 140, minWidth: 80 },
  { field: "zipCode", headerName: "Zip Code", width: 130, minWidth: 70 },
];

const CONTACTS_COLUMNS_ORDER_KEY = "contacts.columnOrder.v1";
const CONTACTS_COLUMNS_WIDTHS_KEY = "contacts.columnWidths.v1";

const DEFAULT_COLUMN_ORDER = BASE_COLUMNS.map((c) => c.field);
const DEFAULT_COLUMN_WIDTHS = Object.fromEntries(BASE_COLUMNS.map((c) => [c.field, c.width]));

const getColumnByField = (field) => BASE_COLUMNS.find((c) => c.field === field);

const readJsonStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const normalizeOrder = (order) => {
  if (!Array.isArray(order)) return DEFAULT_COLUMN_ORDER;
  const valid = order.filter((field) => getColumnByField(field));
  const missing = DEFAULT_COLUMN_ORDER.filter((field) => !valid.includes(field));
  return [...valid, ...missing];
};

const normalizeWidths = (widths) => {
  if (!widths || typeof widths !== "object") return DEFAULT_COLUMN_WIDTHS;
  const out = { ...DEFAULT_COLUMN_WIDTHS };
  for (const field of DEFAULT_COLUMN_ORDER) {
    const candidate = Number(widths[field]);
    const minWidth = getColumnByField(field)?.minWidth || 60;
    if (Number.isFinite(candidate) && candidate > 0) out[field] = Math.max(minWidth, candidate);
  }
  return out;
};

const Contacts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columnOrder, setColumnOrder] = useState(() =>
    normalizeOrder(readJsonStorage(CONTACTS_COLUMNS_ORDER_KEY, DEFAULT_COLUMN_ORDER))
  );
  const [columnWidths, setColumnWidths] = useState(() =>
    normalizeWidths(readJsonStorage(CONTACTS_COLUMNS_WIDTHS_KEY, DEFAULT_COLUMN_WIDTHS))
  );

  const measureAutoFitWidth = useCallback((field, headerName) => {
    const values = rows.map((r) => (r?.[field] == null ? "" : String(r[field])));
    const maxLen = Math.max(headerName.length, ...values.map((v) => v.length), 4);
    const estimated = Math.round(maxLen * 8.6 + 38);
    const minWidth = BASE_COLUMNS.find((c) => c.field === field)?.minWidth || 90;
    return Math.max(minWidth, Math.min(estimated, 460));
  }, [rows]);

  const columns = useMemo(
    () =>
      columnOrder
        .map((field) => {
          const col = getColumnByField(field);
          if (!col) return null;
          return {
            ...col,
            width: columnWidths[col.field] || col.width,
            resizable: true,
          };
        })
        .filter(Boolean),
    [columnOrder, columnWidths]
  );

  useEffect(() => {
    localStorage.setItem(CONTACTS_COLUMNS_ORDER_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem(CONTACTS_COLUMNS_WIDTHS_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/brands");
        const detailedBrands = await Promise.all(
          (data || []).map(async (brand) => {
            try {
              const detail = await api.get(`/api/brands/${brand.id}`);
              return { ...brand, ...(detail.data || {}) };
            } catch {
              return brand;
            }
          })
        );
        setRows(
          detailedBrands.map((brand, index) => ({
            id: brand.id || brand._id || `row-${index + 1}`,
            srNo: index + 1,
            registrationId:
              brand.registrationId ||
              brand.registrarId ||
              `REG-${String(brand.id || brand._id || index + 1).slice(-6).toUpperCase()}`,
            contactName: brand.contactName || brand.clientName || brand.name || "",
            brandName: brand.brandName || "",
            phoneNumber: brand.phoneNumber || brand.contactNumber || brand.phone || "",
            emailId: brand.emailId || brand.email || "",
            contactPerson: brand.contactPerson || "",
            website: brand.website || "",
            productDescription: brand.productDescription || "",
            address: brand.address || "",
            city: brand.city || getCityFromAddress(brand.address || ""),
            zipCode: brand.zipCode || brand.zip || getZipFromAddress(brand.address || ""),
          }))
        );
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleResetColumns = () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    localStorage.removeItem(CONTACTS_COLUMNS_ORDER_KEY);
    localStorage.removeItem(CONTACTS_COLUMNS_WIDTHS_KEY);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="12px" flexWrap="wrap">
        <Header title="CONTACTS" subtitle="List of Contacts for Future Reference" />
        <Button variant="outlined" color="secondary" onClick={handleResetColumns}>
          Reset Columns
        </Button>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "@media print": {
            m: 0,
            p: 0,
            height: "auto",
            "& .MuiDataGrid-root": {
              backgroundColor: "#ffffff !important",
              color: "#000000 !important",
              border: "1px solid #d9d9d9 !important",
              fontSize: "11px !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              color: "#000000 !important",
              borderBottom: "1px solid #d9d9d9 !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#000000 !important",
              fontWeight: "600 !important",
            },
            "& .MuiDataGrid-cell": {
              color: "#000000 !important",
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e8e8e8 !important",
              py: "2px !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff !important",
            },
            "& .MuiDataGrid-footerContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-toolbarContainer": {
              display: "none !important",
            },
            "& .name-column--cell": {
              color: "#000000 !important",
            },
          },
        }}
      >
        <DataGrid
          rows={rows}
          loading={loading}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          disableColumnReorder={false}
          onColumnWidthChange={(params) => {
            setColumnWidths((prev) => ({ ...prev, [params.colDef.field]: params.width }));
          }}
          onColumnOrderChange={(params) => {
            const movedField = params.column?.field;
            const targetIndex = params.targetIndex;
            if (!movedField || !Number.isInteger(targetIndex)) return;
            setColumnOrder((prev) => {
              const current = [...prev];
              const fromIndex = current.indexOf(movedField);
              if (fromIndex === -1) return prev;
              current.splice(fromIndex, 1);
              current.splice(targetIndex, 0, movedField);
              return normalizeOrder(current);
            });
          }}
          onColumnHeaderDoubleClick={(params) => {
            const autoWidth = measureAutoFitWidth(params.field, params.colDef.headerName || params.field);
            setColumnWidths((prev) => ({ ...prev, [params.field]: autoWidth }));
          }}
          disableColumnMenu={false}
          sx={{
            "& .MuiDataGrid-columnSeparator": {
              cursor: "col-resize",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Contacts;
